import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  SwapHoriz as SwapHorizIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  VpnKey as VpnKeyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import DataTable from '../components/DataTable';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../services/api';

const Users = () => {
  const { user, isAdmin } = useAuth();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useMockData, setUseMockData] = useState(false);

  // Filter states
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [roleChangeDialogOpen, setRoleChangeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client',
    password: '',
    send_welcome_email: true,
  });
  const [newRole, setNewRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  // Mock data for fallback
  const mockUsers = [
    {
      id: 1,
      email: 'admin@paceestate.com',
      first_name: 'John',
      last_name: 'Administrator',
      phone: '+1 (555) 123-4567',
      role: 'admin',
      is_active: true,
      email_verified: true,
      last_login: '2025-12-06T10:30:00Z',
      created_at: '2025-01-01T00:00:00Z',
    },
    {
      id: 2,
      email: 'sarah.employee@paceestate.com',
      first_name: 'Sarah',
      last_name: 'Johnson',
      phone: '+1 (555) 234-5678',
      role: 'employee',
      is_active: true,
      email_verified: true,
      last_login: '2025-12-05T16:45:00Z',
      created_at: '2025-02-15T00:00:00Z',
    },
    {
      id: 3,
      email: 'mike.client@example.com',
      first_name: 'Mike',
      last_name: 'Smith',
      phone: '+1 (555) 345-6789',
      role: 'client',
      is_active: true,
      email_verified: true,
      last_login: '2025-12-04T09:20:00Z',
      created_at: '2025-03-20T00:00:00Z',
    },
    {
      id: 4,
      email: 'inactive.user@example.com',
      first_name: 'Inactive',
      last_name: 'User',
      phone: '+1 (555) 456-7890',
      role: 'client',
      is_active: false,
      email_verified: false,
      last_login: null,
      created_at: '2025-04-10T00:00:00Z',
    },
    {
      id: 5,
      email: 'emma.employee@paceestate.com',
      first_name: 'Emma',
      last_name: 'Williams',
      phone: '+1 (555) 567-8901',
      role: 'employee',
      is_active: true,
      email_verified: true,
      last_login: '2025-12-06T08:15:00Z',
      created_at: '2025-05-05T00:00:00Z',
    },
  ];

  useEffect(() => {
    if (!isAdmin) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || response.data);
      setUseMockData(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setUseMockData(true);
        setUsers(mockUsers);
        setError('Admin API not available. Showing mock data for demonstration.');
      } else {
        setError(err.response?.data?.error || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  // Computed statistics
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => u.is_active).length;
    const admins = users.filter(u => u.role === 'admin').length;
    const employees = users.filter(u => u.role === 'employee').length;
    const clients = users.filter(u => u.role === 'client').length;

    return {
      total,
      active,
      inactive: total - active,
      admins,
      employees,
      clients,
    };
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      const roleMatch = roleFilter === 'all' || u.role === roleFilter;
      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'active' && u.is_active) ||
        (statusFilter === 'inactive' && !u.is_active);
      return roleMatch && statusMatch;
    });
  }, [users, roleFilter, statusFilter]);

  // Handlers
  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleCreateUser = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'client',
      password: generatePassword(),
      send_welcome_email: true,
    });
    setCreateModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      role: user.role,
      password: '',
      send_welcome_email: false,
    });
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleRoleChange = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleChangeDialogOpen(true);
    handleMenuClose();
  };

  const handleToggleStatus = async (user) => {
    handleMenuClose();
    setActionLoading(true);
    try {
      if (useMockData) {
        setUsers(users.map(u =>
          u.id === user.id ? { ...u, is_active: !u.is_active } : u
        ));
      } else {
        await api.put(`/admin/users/${user.id}/status`, {
          is_active: !user.is_active,
        });
        await fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleResetPassword = async (user) => {
    handleMenuClose();
    setActionLoading(true);
    try {
      if (useMockData) {
        alert('Password reset email would be sent to: ' + user.email);
      } else {
        await api.post(`/admin/users/${user.id}/reset-password`);
        alert('Password reset email sent successfully');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const submitCreateUser = async () => {
    setActionLoading(true);
    try {
      if (useMockData) {
        const newUser = {
          id: users.length + 1,
          ...formData,
          is_active: true,
          email_verified: false,
          last_login: null,
          created_at: new Date().toISOString(),
        };
        setUsers([...users, newUser]);
        setCreateModalOpen(false);
      } else {
        await api.post('/admin/users', formData);
        await fetchUsers();
        setCreateModalOpen(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  const submitEditUser = async () => {
    setActionLoading(true);
    try {
      if (useMockData) {
        setUsers(users.map(u =>
          u.id === selectedUser.id ? { ...u, ...formData } : u
        ));
        setEditModalOpen(false);
      } else {
        await api.put(`/admin/users/${selectedUser.id}`, formData);
        await fetchUsers();
        setEditModalOpen(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const submitRoleChange = async () => {
    setActionLoading(true);
    try {
      if (useMockData) {
        setUsers(users.map(u =>
          u.id === selectedUser.id ? { ...u, role: newRole } : u
        ));
        setRoleChangeDialogOpen(false);
      } else {
        await api.put(`/admin/users/${selectedUser.id}/role`, { role: newRole });
        await fetchUsers();
        setRoleChangeDialogOpen(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change user role');
    } finally {
      setActionLoading(false);
    }
  };

  const submitDeleteUser = async () => {
    setActionLoading(true);
    try {
      if (useMockData) {
        setUsers(users.filter(u => u.id !== selectedUser.id));
        setDeleteDialogOpen(false);
      } else {
        await api.delete(`/admin/users/${selectedUser.id}`);
        await fetchUsers();
        setDeleteDialogOpen(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'secondary';
      case 'employee':
        return 'primary';
      case 'client':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // DataTable columns
  const columns = [
    {
      field: 'user',
      headerName: 'User',
      sortable: true,
      searchable: true,
      minWidth: 250,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: getRoleColor(row.role) + '.main',
              width: 40,
              height: 40,
            }}
          >
            {getInitials(row.first_name, row.last_name)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.first_name} {row.last_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      sortable: true,
      searchable: true,
      minWidth: 150,
    },
    {
      field: 'role',
      headerName: 'Role',
      sortable: true,
      minWidth: 120,
      render: (row) => (
        <Chip
          label={row.role}
          color={getRoleColor(row.role)}
          size="small"
          sx={{ textTransform: 'capitalize', fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      sortable: true,
      minWidth: 100,
      render: (row) => (
        <Chip
          label={row.is_active ? 'Active' : 'Inactive'}
          color={row.is_active ? 'success' : 'default'}
          size="small"
          variant={row.is_active ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      sortable: true,
      minWidth: 180,
      render: (row) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(row.last_login)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'right',
      minWidth: 80,
      render: (row) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, row)}
          disabled={actionLoading}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Access denied. You need administrator privileges to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity={useMockData ? 'info' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  By Role
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Admins:</Typography>
                    <Chip label={stats.admins} color="secondary" size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Employees:</Typography>
                    <Chip label={stats.employees} color="primary" size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Clients:</Typography>
                    <Chip label={stats.clients} color="success" size="small" />
                  </Box>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                  <BlockIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.inactive}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Filter by Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="client">Client</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" textAlign="right">
                Showing {filteredUsers.length} of {users.length} users
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search by name or email..."
        emptyMessage="No users found"
        defaultRowsPerPage={10}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(menuUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleRoleChange(menuUser)}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Role</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleResetPassword(menuUser)}>
          <ListItemIcon>
            <VpnKeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reset Password</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleToggleStatus(menuUser)}>
          <ListItemIcon>
            {menuUser?.is_active ? (
              <BlockIcon fontSize="small" color="warning" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>
            {menuUser?.is_active ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleDeleteUser(menuUser)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create User Modal */}
      <FormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={submitCreateUser}
        title="Create New User"
        saveLabel="Create User"
        loading={actionLoading}
        maxWidth="sm"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="client">Client</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Temporary Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <Button
                    size="small"
                    onClick={() => setFormData({ ...formData, password: generatePassword() })}
                  >
                    Generate
                  </Button>
                ),
              }}
              helperText="User will be required to change password on first login"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.send_welcome_email}
                  onChange={(e) => setFormData({ ...formData, send_welcome_email: e.target.checked })}
                />
              }
              label="Send welcome email with login credentials"
            />
          </Grid>
        </Grid>
      </FormModal>

      {/* Edit User Modal */}
      <FormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={submitEditUser}
        title={`Edit User: ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        saveLabel="Save Changes"
        loading={actionLoading}
        maxWidth="sm"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              User Information
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Role:</Typography>
                <Chip label={selectedUser?.role} color={getRoleColor(selectedUser?.role)} size="small" />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Chip
                  label={selectedUser?.is_active ? 'Active' : 'Inactive'}
                  color={selectedUser?.is_active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Created:</Typography>
                <Typography variant="body2">{formatDate(selectedUser?.created_at)}</Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              Quick Actions
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                onClick={() => {
                  setEditModalOpen(false);
                  handleRoleChange(selectedUser);
                }}
                fullWidth
              >
                Change Role
              </Button>
              <Button
                variant="outlined"
                startIcon={<VpnKeyIcon />}
                onClick={() => {
                  setEditModalOpen(false);
                  handleResetPassword(selectedUser);
                }}
                fullWidth
              >
                Reset Password
              </Button>
              <Button
                variant="outlined"
                color={selectedUser?.is_active ? 'warning' : 'success'}
                startIcon={selectedUser?.is_active ? <BlockIcon /> : <CheckCircleIcon />}
                onClick={() => {
                  setEditModalOpen(false);
                  handleToggleStatus(selectedUser);
                }}
                fullWidth
              >
                {selectedUser?.is_active ? 'Deactivate User' : 'Activate User'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </FormModal>

      {/* Role Change Dialog */}
      <ConfirmDialog
        open={roleChangeDialogOpen}
        onClose={() => setRoleChangeDialogOpen(false)}
        onConfirm={submitRoleChange}
        title="Change User Role"
        message={
          <Box>
            <Typography gutterBottom>
              Change role for {selectedUser?.first_name} {selectedUser?.last_name}?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>New Role</InputLabel>
              <Select
                value={newRole}
                label="New Role"
                onChange={(e) => setNewRole(e.target.value)}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="client">Client</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will change the user's permissions immediately.
            </Alert>
          </Box>
        }
        confirmLabel="Change Role"
        loading={actionLoading}
      />

      {/* Delete User Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={submitDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUser?.first_name} ${selectedUser?.last_name}? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={actionLoading}
        danger
      />
    </Box>
  );
};

export default Users;
